import { z } from 'zod';
import { ADMIN_ROLES } from './access.js';
import { emailSchema } from './auth.js';

/**
 * Pengguna panel admin, dikelola super admin di menu Admin User.
 * Kata sandi akun panel hanya dibuat dan diganti super admin; penggunanya
 * tidak punya jalur ganti sandi sendiri.
 */

/**
 * Akun panel (STAFF/ADMIN/SUPER_ADMIN) dikunci setelah sekian kali salah sandi
 * berturut-turut, sampai dibuka super admin. Akun pelanggan tidak dikunci.
 */
export const ADMIN_LOGIN_LOCK_THRESHOLD = 5;

export const adminPasswordSchema = z
  .string()
  .min(10, 'Kata sandi minimal 10 karakter')
  .max(200)
  .refine((value) => /[a-zA-Z]/.test(value) && /[0-9]/.test(value), {
    message: 'Kata sandi harus memuat huruf dan angka',
  });

export const adminUserRoleSchema = z.enum(ADMIN_ROLES);

/**
 * Role akun panel: `SUPER_ADMIN` (akses penuh, tidak diatur) atau id role
 * buatan super admin di halaman Users & Roles.
 */
export const panelRoleRefSchema = z.string().trim().min(1).max(64);

/** Nama masuk panel admin: 3-32 huruf kecil, angka, titik, atau garis bawah. */
export const ADMIN_USERNAME_PATTERN = /^[a-z0-9._]{3,32}$/;

export const adminUsernameSchema = z
  .string()
  .trim()
  .transform((value) => value.toLowerCase())
  .pipe(
    z
      .string()
      .min(3, 'Username minimal 3 karakter')
      .max(32, 'Username maksimal 32 karakter')
      .regex(
        ADMIN_USERNAME_PATTERN,
        'Username hanya boleh huruf kecil, angka, titik, dan garis bawah',
      ),
  );

/** Email akun panel opsional (akun internal); string kosong dianggap tidak diisi. */
const optionalEmailSchema = z
  .union([z.literal(''), emailSchema])
  .transform((value) => (value === '' ? null : value))
  .nullable();

export const adminUserCreateSchema = z.object({
  name: z.string().trim().min(2, 'Nama minimal 2 karakter').max(120),
  username: adminUsernameSchema,
  email: optionalEmailSchema.default(null),
  role: panelRoleRefSchema,
  password: adminPasswordSchema,
});

/** Mengangkat akun yang sudah terdaftar (mis. pelanggan) menjadi pengguna panel. */
export const adminUserPromoteSchema = z.object({
  email: emailSchema,
  username: adminUsernameSchema,
  role: panelRoleRefSchema,
});

export const adminUserUpdateSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    username: adminUsernameSchema.optional(),
    /** null atau '' menghapus email. */
    email: optionalEmailSchema.optional(),
    /** Id role panel, `SUPER_ADMIN`, atau `CUSTOMER` untuk mencabut akses panel. */
    role: panelRoleRefSchema.optional(),
    status: z.enum(['ACTIVE', 'SUSPENDED']).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'Tidak ada perubahan yang dikirim' });

export const adminUserPasswordSchema = z.object({ password: adminPasswordSchema });

export const adminUserParamsSchema = z.object({ id: z.string().min(1).max(64) });

export const adminUserSessionParamsSchema = z.object({
  id: z.string().min(1).max(64),
  sessionId: z.string().min(1).max(64),
});

export type AdminUserCreate = z.infer<typeof adminUserCreateSchema>;
export type AdminUserPromote = z.infer<typeof adminUserPromoteSchema>;
export type AdminUserUpdate = z.infer<typeof adminUserUpdateSchema>;
